/**
 * s-hero-slider — homepage hero carousel.
 * Native horizontal scroll-snap does the sliding (swipe works for free); this element adds
 * prev/next/dot/pause commands, autoplay (skipped for reduced-motion users, paused
 * only by keyboard focus or the pause button), dot state, and inert off-screen slides so keyboard focus stays on the visible one.
 */
class SHeroSlider extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.toggleButton = this.querySelector('[data-toggle]');
    this.index = 0;
    this.delay = Number(this.dataset.delay) || 6000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.userPaused = this.reducedMotion;
    this.hoverPaused = false;

    this.addEventListener('command', (event) => this.onCommand(event), { signal });
    this.track.addEventListener('scroll', () => this.onScroll(), { signal, passive: true });
    this.addEventListener('focusin', (event) => {
      if (!event.target.matches?.(':focus-visible')) return;
      this.hoverPaused = true;
      this.stop();
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.hoverPaused = false;
      this.start();
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else this.start();
    }, { signal });

    this.update();
    this.syncToggle();
    this.start();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.stop();
  }

  onCommand(event) {
    const command = event.command;
    if (command === '--next') this.go(this.index + 1);
    else if (command === '--prev') this.go(this.index - 1);
    else if (command === '--go') this.go(Number(event.source?.dataset.index || 0));
    if (command !== '--toggle') this.start();
    else if (command === '--toggle') {
      this.userPaused = !this.userPaused;
      this.syncToggle();
      if (this.userPaused) this.stop();
      else this.start();
    }
  }

  go(target) {
    const count = this.slides.length;
    const next = ((target % count) + count) % count;
    const slide = this.slides[next];
    this.track.scrollTo({ left: slide.offsetLeft, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    this.index = next;
    this.update();
  }

  onScroll() {
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      const width = this.track.clientWidth || 1;
      const current = Math.round(Math.abs(this.track.scrollLeft) / width);
      if (current !== this.index) {
        this.index = Math.min(current, this.slides.length - 1);
        this.update();
      }
    }, 80);
  }

  update() {
    this.slides.forEach((slide, i) => {
      slide.inert = i !== this.index;
    });
    this.dots.forEach((dot, i) => {
      if (i === this.index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  syncToggle() {
    if (!this.toggleButton) return;
    const label = this.userPaused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause;
    this.toggleButton.setAttribute('aria-label', label);
    this.toggleButton.dataset.state = this.userPaused ? 'paused' : 'playing';
  }

  start() {
    this.stop();
    if (this.userPaused || this.hoverPaused || this.slides.length < 2) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.delay);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('s-hero-slider')) {
  customElements.define('s-hero-slider', SHeroSlider);
}
